package com.speedsoftware.nibbler.ui.boards

import androidx.compose.ui.draw.clip
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.speedsoftware.nibbler.models.ProjectMemberDto
import com.speedsoftware.nibbler.models.UserDto
import com.speedsoftware.nibbler.ui.components.PropertyIcon
import com.speedsoftware.nibbler.ui.components.PropertyIconView
import com.speedsoftware.nibbler.ui.components.UserAvatar
import com.speedsoftware.nibbler.ui.theme.NibblerColors

/**
 * Multi-select for the people on an issue. The chosen people are shown as removable chips; "Add" opens a
 * dropdown with a search box and a checkmark next to everybody who is already selected.
 */
@OptIn(ExperimentalLayoutApi::class)
@Composable
fun AssigneeSelector(
    members: List<ProjectMemberDto>,
    selectedIds: List<String>,
    onChange: (List<String>) -> Unit,
    modifier: Modifier = Modifier
) {
    var expanded by remember { mutableStateOf(false) }
    var query by remember { mutableStateOf("") }
    val searchFocus = remember { FocusRequester() }

    val users = remember(members) { members.map { it.toUser() } }
    val selected = selectedIds.mapNotNull { id -> users.firstOrNull { it.id == id } }
    val visible = users.filter {
        query.isBlank() || it.name.contains(query, ignoreCase = true) || it.email.contains(query, ignoreCase = true)
    }

    FlowRow(
        modifier = modifier,
        horizontalArrangement = Arrangement.spacedBy(6.dp),
        verticalArrangement = Arrangement.spacedBy(6.dp)
    ) {
        selected.forEach { user ->
            AssigneeChip(user = user, onRemove = { onChange(selectedIds - user.id) })
        }

        Box {
            Row(
                modifier = Modifier
                    .clip(RoundedCornerShape(8.dp))
                    .clickable { query = ""; expanded = true }
                    .border(1.dp, NibblerColors.OutlineVariant, RoundedCornerShape(8.dp))
                    .padding(horizontal = 10.dp, vertical = 5.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(6.dp)
            ) {
                PropertyIconView(PropertyIcon.PEOPLE, size = 14.dp)
                Text(if (selected.isEmpty()) "Assign people" else "Add", fontSize = 12.sp, color = NibblerColors.OnSurface)
            }

            DropdownMenu(
                expanded = expanded,
                onDismissRequest = { expanded = false },
                modifier = Modifier.width(240.dp).background(NibblerColors.SurfaceContainerHigh)
            ) {
                OutlinedTextField(
                    value = query,
                    onValueChange = { query = it },
                    placeholder = { Text("Search people", fontSize = 13.sp, color = NibblerColors.OnSurfaceVariant) },
                    singleLine = true,
                    shape = RoundedCornerShape(8.dp),
                    modifier = Modifier.fillMaxWidth().padding(horizontal = 10.dp, vertical = 4.dp).focusRequester(searchFocus)
                )

                Row(
                    modifier = Modifier.fillMaxWidth().padding(horizontal = 14.dp, vertical = 4.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text("${selectedIds.size} selected", fontSize = 11.sp, color = NibblerColors.OnSurfaceVariant)
                    if (selectedIds.isNotEmpty()) {
                        Text(
                            "Clear",
                            fontSize = 11.sp,
                            fontWeight = FontWeight.Bold,
                            color = NibblerColors.BauhausYellow,
                            modifier = Modifier.clickable { onChange(emptyList()) }
                        )
                    }
                }

                Column(modifier = Modifier.heightIn(max = 240.dp).verticalScroll(rememberScrollState())) {
                    if (visible.isEmpty()) {
                        Text(
                            "No people found",
                            fontSize = 12.sp,
                            color = NibblerColors.OnSurfaceVariant,
                            modifier = Modifier.padding(horizontal = 14.dp, vertical = 10.dp)
                        )
                    }
                    visible.forEach { user ->
                        val isSelected = user.id in selectedIds
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .background(if (isSelected) NibblerColors.SecondaryContainer.copy(alpha = 0.45f) else NibblerColors.SurfaceContainerHigh)
                                .clickable { onChange(if (isSelected) selectedIds - user.id else selectedIds + user.id) }
                                .padding(horizontal = 14.dp, vertical = 8.dp),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(10.dp)
                        ) {
                            UserAvatar(user, 26.dp)
                            Column(modifier = Modifier.weight(1f)) {
                                Text(user.name, fontSize = 13.sp, fontWeight = FontWeight.Medium, color = NibblerColors.OnSurface)
                                Text(user.email, fontSize = 11.sp, color = NibblerColors.OnSurfaceVariant)
                            }
                            if (isSelected) PropertyIconView(PropertyIcon.CHECK, size = 16.dp, color = NibblerColors.BauhausYellow)
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun AssigneeChip(user: UserDto, onRemove: () -> Unit) {
    Row(
        modifier = Modifier
            .background(NibblerColors.SurfaceContainer, RoundedCornerShape(8.dp))
            .border(1.dp, NibblerColors.OutlineVariant, RoundedCornerShape(8.dp))
            .padding(start = 6.dp, end = 4.dp, top = 3.dp, bottom = 3.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(6.dp)
    ) {
        UserAvatar(user, 20.dp)
        Text(user.name, fontSize = 12.sp, color = NibblerColors.OnSurface)
        Box(
            modifier = Modifier.size(18.dp).clickable { onRemove() },
            contentAlignment = Alignment.Center
        ) {
            PropertyIconView(PropertyIcon.CLOSE, size = 10.dp)
        }
    }
}
